import { useMedplum } from "@medplum/react-hooks";
import { markNotificationRead, type NotificationView, useNotifications } from "@repo/data-store";
import { Link } from "@tanstack/react-router";
import React, { useEffect, useRef } from "react";
import { toast } from "sonner";
import { practitionerFhirIdFromProfile } from "@/shared/hooks/use-current-practitioner";

/** Verb per notification type, derived from Communication.category. */
const NOTIFICATION_VERB: Record<string, string> = {
  assigned: "assigned you to",
  mentioned: "mentioned you on",
};

function getNotificationMessage(comm: NotificationView): string {
  const code = comm.category?.[0]?.coding?.[0]?.code ?? "";
  const verb = NOTIFICATION_VERB[code] ?? "updated";
  const senderName = comm.sender?.display;
  const taskPhrase = comm.subjectTask?.number
    ? `task ${comm.subjectTask.number}${comm.subjectTask.description ? ` ${comm.subjectTask.description}` : ""}`
    : (comm.subjectTask?.description ?? "a task");
  return `${senderName ?? "Someone"} ${verb} ${taskPhrase}`;
}

function getNotificationLink(comm: NotificationView): string | undefined {
  const taskId = comm.source?.resourceType === "Task" ? comm.source.taskId : undefined;
  return taskId ? `/tasks/${taskId}` : undefined;
}

/** Show a sonner toast for each new unread notification */
export function useNotificationToast() {
  const medplum = useMedplum();
  const practitionerId = practitionerFhirIdFromProfile(medplum.getProfile());
  const { data: notifications } = useNotifications({ practitionerId });
  const mountedAtRef = useRef(new Date().toISOString());
  const seenIdsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!notifications) return;

    for (const comm of notifications) {
      if (!comm.id || seenIdsRef.current.has(comm.id)) continue;
      seenIdsRef.current.add(comm.id);
      if (comm.unread && comm.sent && comm.sent > mountedAtRef.current) {
        const link = getNotificationLink(comm);
        const message = getNotificationMessage(comm);
        const commId = comm.id;

        toast(
          React.createElement(
            link ? Link : "div",
            link
              ? {
                  to: link,
                  onClick: () => {
                    markNotificationRead({ communicationId: commId });
                    toast.dismiss();
                  },
                  className: "-m-4 p-4 block no-underline text-inherit",
                }
              : null,
            message,
          ),
          { className: link ? "[&_[data-content]]:!p-0" : undefined },
        );
      }
    }
  }, [notifications]);
}
